import { useCallback, useRef, useState } from "react";
import Swal from "sweetalert2";
import { pruneEmptyHaklaiAnalyzeDraftRows } from "../calc/shared/haklaiDraftCleanupHelpers";
import { buildHaklaiDraftRawPayload } from "../calc/shared/haklaiRawPayloadHelpers";
import {
  analyzeHaklaiDeal,
  updateHaklaiDealData
} from "../services/haklaiDealService";
import { validateHaklaiDepreciationsForSave } from "../domain/haklaiValidation";
import { isHaklaiAnalysisFullyExempt } from "../domain/haklaiAnalysisOutcome";
import { HAKLAI_SAVE_ANALYZE_MESSAGES } from "./haklaiHookTexts";

const useHaklaiSaveAnalyze = ({
  calc,
  dealId,
  dismissHaklaiNotice,
  history,
  isDealDateChanged,
  manage,
  resultsNavKey,
  setAnalyzedClassicCalc,
  setCalc,
  markHaklaiAnalysisCurrent,
  setCurrentNavFocused,
  setHaklaiAnalysis,
  setIsDealDateChanged,
  validateForAnalyze
}) => {
  const [haklaiActionInFlight, setHaklaiActionInFlight] = useState(false);
  const haklaiActionInFlightRef = useRef(false);

  const buildHaklaiRawForSave = useCallback((sourceCalc = calc) => (
    buildHaklaiDraftRawPayload(sourceCalc)
  ), [calc]);

  const buildHaklaiCalcForSave = useCallback((sourceCalc = calc) => {
    return { haklaiRaw: buildHaklaiRawForSave(sourceCalc) };
  }, [buildHaklaiRawForSave, calc]);

  const saveHaklaiDraft = useCallback(
    (sourceCalc = calc) => updateHaklaiDealData(dealId, buildHaklaiCalcForSave(sourceCalc), manage, isDealDateChanged),
    [dealId, buildHaklaiCalcForSave, manage, isDealDateChanged, calc]
  );

  const saveHaklaiManage = useCallback(
    () => updateHaklaiDealData(dealId, undefined, manage, false),
    [dealId, manage]
  );

  const prepareAnalyzeCalc = useCallback((sourceCalc = calc) => {
    const pruneState = pruneEmptyHaklaiAnalyzeDraftRows(sourceCalc);
    if (pruneState.changed) {
      setCalc(pruneState.calc);
    }
    return pruneState;
  }, [calc, setCalc]);

  const beginHaklaiAction = useCallback(() => {
    if (haklaiActionInFlightRef.current) return false;
    haklaiActionInFlightRef.current = true;
    setHaklaiActionInFlight(true);
    return true;
  }, []);

  const finishHaklaiAction = useCallback(() => {
    haklaiActionInFlightRef.current = false;
    setHaklaiActionInFlight(false);
  }, []);

  const persistDeal = useCallback(
    (successMessage) => {
      const depreciationError = validateHaklaiDepreciationsForSave(calc);
      if (depreciationError) {
        Swal.fire("", depreciationError, "error");
        return;
      }
      if (!beginHaklaiAction()) return;
      saveHaklaiDraft().then((res) => {
        if (res.success) {
          Swal.fire(successMessage || HAKLAI_SAVE_ANALYZE_MESSAGES.dealSaved, "", "success");
          if (isDealDateChanged) setIsDealDateChanged(false);
        } else {
          Swal.fire(
            HAKLAI_SAVE_ANALYZE_MESSAGES.genericErrorTitle,
            res.message || HAKLAI_SAVE_ANALYZE_MESSAGES.saveFailed,
            "error"
          );
        }
      }).catch(() => {
        Swal.fire(
          HAKLAI_SAVE_ANALYZE_MESSAGES.genericErrorTitle,
          HAKLAI_SAVE_ANALYZE_MESSAGES.saveFailed,
          "error"
        );
      }).finally(() => {
        finishHaklaiAction();
      });
    },
    [beginHaklaiAction, calc, finishHaklaiAction, saveHaklaiDraft, isDealDateChanged, setIsDealDateChanged]
  );

  const updateDealData = useCallback(() => {
    persistDeal(HAKLAI_SAVE_ANALYZE_MESSAGES.dealSaved);
  }, [persistDeal]);

  const updateManageData = useCallback(() => {
    if (!beginHaklaiAction()) return;
    saveHaklaiManage().then((res) => {
      if (res.success) {
        Swal.fire(HAKLAI_SAVE_ANALYZE_MESSAGES.dealSaved, "", "success");
      } else {
        Swal.fire(
          HAKLAI_SAVE_ANALYZE_MESSAGES.genericErrorTitle,
          res.message || HAKLAI_SAVE_ANALYZE_MESSAGES.saveFailed,
          "error"
        );
      }
    }).catch(() => {
      Swal.fire(
        HAKLAI_SAVE_ANALYZE_MESSAGES.genericErrorTitle,
        HAKLAI_SAVE_ANALYZE_MESSAGES.saveFailed,
        "error"
      );
    }).finally(() => {
      finishHaklaiAction();
    });
  }, [beginHaklaiAction, finishHaklaiAction, saveHaklaiManage]);

  const analyzeDeal = useCallback((sourceCalc = calc) => {
    const pruneState = prepareAnalyzeCalc(sourceCalc);
    const calcForAnalyze = pruneState.calc;

    const err = validateForAnalyze(calcForAnalyze);
    if (err) {
      Swal.fire({
        title: "",
        text: err,
        icon: "error",
        returnFocus: false
      });
      return;
    }
    if (!beginHaklaiAction()) return;
    saveHaklaiDraft(calcForAnalyze).then((saveRes) => {
      if (!saveRes.success) {
        Swal.fire(
          HAKLAI_SAVE_ANALYZE_MESSAGES.genericErrorTitle,
          saveRes.message || HAKLAI_SAVE_ANALYZE_MESSAGES.saveBeforeAnalyzeFailed,
          "error"
        );
        return;
      }

      if (isDealDateChanged) setIsDealDateChanged(false);
      return analyzeHaklaiDeal(dealId).then((analyzeRes) => {
        if (analyzeRes.success) {
          const analyzedPreCalc = analyzeRes.data?.preCalc || null;
          const isFullyExemptAnalysis = isHaklaiAnalysisFullyExempt(analyzedPreCalc);
          setHaklaiAnalysis(analyzeRes.data?.haklaiAnalysis || null);
          setAnalyzedClassicCalc({
            preCalc: analyzedPreCalc,
            mainCalc: analyzeRes.data?.mainCalc || null
          });
          if (typeof markHaklaiAnalysisCurrent === "function") {
            markHaklaiAnalysisCurrent(calcForAnalyze);
          }
          dismissHaklaiNotice();
          if (!isFullyExemptAnalysis) {
            setCurrentNavFocused(resultsNavKey);
            history.push("/haklai/results/" + dealId);
            Swal.fire(HAKLAI_SAVE_ANALYZE_MESSAGES.analyzeSaved, "", "success");
          }
        } else {
          Swal.fire(
            HAKLAI_SAVE_ANALYZE_MESSAGES.genericErrorTitle,
            analyzeRes.message || HAKLAI_SAVE_ANALYZE_MESSAGES.analyzeFailed,
            "error"
          );
        }
      }).catch(() => {
        Swal.fire(
          HAKLAI_SAVE_ANALYZE_MESSAGES.genericErrorTitle,
          HAKLAI_SAVE_ANALYZE_MESSAGES.analyzeFailed,
          "error"
        );
      });
    }).catch(() => {
      Swal.fire(
        HAKLAI_SAVE_ANALYZE_MESSAGES.genericErrorTitle,
        HAKLAI_SAVE_ANALYZE_MESSAGES.saveBeforeAnalyzeFailed,
        "error"
      );
    }).finally(() => {
      finishHaklaiAction();
    });
  }, [
    beginHaklaiAction,
    calc,
    dealId,
    dismissHaklaiNotice,
    finishHaklaiAction,
    history,
    isDealDateChanged,
    prepareAnalyzeCalc,
    resultsNavKey,
    saveHaklaiDraft,
    setAnalyzedClassicCalc,
    markHaklaiAnalysisCurrent,
    setCurrentNavFocused,
    setHaklaiAnalysis,
    setIsDealDateChanged,
    validateForAnalyze
  ]);

  return {
    analyzeDeal,
    haklaiActionInFlight,
    prepareAnalyzeCalc,
    updateDealData,
    updateManageData
  };
};

export default useHaklaiSaveAnalyze;
